import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCandidates } from "../api";
import PopupMessage from "./PopupMessage";
import { primarySkillOptions, secondarySkillOptions, currentLocations, workTypeOptions, roleOptions, feedbackStatusOptions, sourceOptions } from "../services/dropdownOptions";
import { CANDIDATE_TABLE_COLUMNS, INITIAL_CANDIDATE_FILTERS, PAGINATION_SETTINGS } from "../services/commonConstants";

const CandidatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    name: "",
    role: "",
    location: "",
    skills: "",
    experience: "",
    workType: "",
    feedbackStatus: location.state?.initialFilters?.feedbackStatus || "",
    readyToTravel: "",
    remoteAvailable: "",
    source: "",
    // noticePeriod: "", // COMMENTED OUT - Notice period filter disabled
    availableFromStart: "",
    availableFromEnd: ""
  });

  // Fetch candidates on component mount
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        console.log('🚀 CandidatePage: Starting fetch candidates...');
        setLoading(true);
        const response = await getCandidates();
        console.log('📥 CandidatePage: Response received:', response);
        if (response.success) {
          console.log('✅ CandidatePage: Success - Candidates loaded:', response.data?.length || 0);
          // Transform API data to match table structure
          const normalizeDate = (value) => {
            if (!value) return null;
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
          };

          const transformedData = response.data.map(candidate => {
            const readyToTravelValue = candidate.ReadyToTravel ?? candidate.readyToTravel;
            const remoteAvailableValue = candidate.RemoteAvailable ?? candidate.remoteAvailable;
            const availableFromValue = normalizeDate(candidate.AvailableFrom ?? candidate.availableFrom);

            return {
              id: candidate.CandidateID,
              name: candidate.FullName,
              email: candidate.Email,
              phone: candidate.Mobile,
              location: candidate.CurrentLocation,
              role: candidate.Role,
              readyToTravel: readyToTravelValue === 1 || readyToTravelValue === '1' || readyToTravelValue === true ? 1 : 0,
              remoteAvailable: remoteAvailableValue === 1 || remoteAvailableValue === '1' || remoteAvailableValue === true ? 1 : 0,
              experience: candidate.YearsOfExperience != null ? candidate.YearsOfExperience : 'Not specified',
              primarySkills: candidate.PrimarySkills || 'Not specified',
              secondarySkills: candidate.SecondarySkills || 'Not specified',
              techStack: candidate.TechStack || 'Not specified',
              skills: candidate.Skills || [candidate.PrimarySkills, candidate.SecondarySkills, candidate.TechStack]
                .filter(Boolean)
                .join(', '),
              workType: candidate.WorkType || 'Not specified',
              // noticePeriod: candidate.NoticePeriod || candidate.noticePeriod || 'Not specified', // COMMENTED OUT - Notice period disabled
              c2c: candidate.CTC,
              expectedCTC: candidate.ExpectedCTC || 'Not specified',
              freelanceRate: candidate.FreelanceRate || 'Not specified',
              referrerCode: candidate.ReferrerCode || 'Not specified',
              source: candidate.Source || 'Not specified',
              status: candidate.Status,
              aadharNo: candidate.AadharNo || 'Not specified',
              pancardNo: candidate.PancardNo || 'Not specified',
              appliedDate: candidate.CreatedAt,
              resumeFile: candidate.OriginalFileName,
              resumeUrl: candidate.StoredPath,
              resumeId: candidate.ResumeID,
              feedbackStatus: candidate.FeedbackStatus || 'No feedback',
              // feedbackComment: candidate.FeedbackComment || 'No feedback', // COMMENTED OUT - Feedback comment column disabled
              availableFrom: availableFromValue
            };
          });
          setCandidates(transformedData);
        } else {
          console.error('❌ CandidatePage: Failed to fetch candidates:', response.message);
          const errorMsg = response.message || 'Failed to load candidates';
          setError(errorMsg);
          setShowErrorPopup(true);
        }
      } catch (err) {
        console.error('💥 CandidatePage: Error fetching candidates:', err);
        console.error('Error Stack:', err.stack);
        const errorMsg = 'Network error while loading candidates';
        setError(errorMsg);
        setShowErrorPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, []);

  // Handle navigation state changes to apply filters
  useEffect(() => {
    if (location.state?.initialFilters) {
      setFilters(prev => ({
        ...prev,
        ...location.state.initialFilters
      }));
      setCurrentPage(1); // Reset to first page when applying new filters
    } else if (location.state?.clearFilters) {
      // Clear all filters when navigating from "Candidates" link
      setFilters({
        name: "",
        role: "",
        location: "",
        skills: "",
        experience: "",
        workType: "",
        feedbackStatus: "",
        readyToTravel: "",
        remoteAvailable: "",
        source: "",
        noticePeriod: "",
        availableFromStart: "",
        availableFromEnd: ""
      });
      setCurrentPage(1);
    }
  }, [location.state]);

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter candidates based on filters
  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      return (
        (filters.name === "" || candidate.name.toLowerCase().includes(filters.name.toLowerCase())) &&
        (filters.role === "" || candidate.role.toLowerCase().includes(filters.role.toLowerCase())) &&
        (filters.location === "" || candidate.location.toLowerCase().includes(filters.location.toLowerCase())) &&
        (filters.skills === "" || candidate.skills.toLowerCase().includes(filters.skills.toLowerCase())) &&
        (filters.experience === "" || !candidate.experience || candidate.experience >= parseInt(filters.experience)) &&
        (filters.workType === "" || candidate.workType.toLowerCase().includes(filters.workType.toLowerCase())) &&
        (filters.feedbackStatus === "" || candidate.feedbackStatus === filters.feedbackStatus) &&
        (filters.readyToTravel === "" || candidate.readyToTravel.toString() === filters.readyToTravel) &&
        (filters.remoteAvailable === "" || candidate.remoteAvailable?.toString() === filters.remoteAvailable) &&
        (filters.source === "" || candidate.source.toLowerCase().includes(filters.source.toLowerCase())) &&
        // (filters.noticePeriod === "" || candidate.noticePeriod.toLowerCase().includes(filters.noticePeriod.toLowerCase())) && // COMMENTED OUT - Notice period filter disabled
        (filters.availableFromStart === "" || (candidate.availableFrom && candidate.availableFrom >= filters.availableFromStart)) &&
        (filters.availableFromEnd === "" || (candidate.availableFrom && candidate.availableFrom <= filters.availableFromEnd))
      );
    });
  }, [candidates, filters]);

  // COMMENTED OUT - Notice period options disabled
  /*
  const noticePeriodOptions = useMemo(() => {
    const values = candidates
      .map(candidate => candidate.noticePeriod)
      .filter(period => period && period.toString().trim() !== '' && period.toString().toLowerCase() !== 'not specified');

    return [...new Set(values)].sort((a, b) => a.toString().localeCompare(b.toString()));
  }, [candidates]);
  */

  const sourceOptions = useMemo(() => {
    const values = candidates
      .map(candidate => candidate.source)
      .filter(source => source && source.toString().trim() !== '' && source.toString().toLowerCase() !== 'not specified');

    return [...new Set(values)].sort((a, b) => a.toString().localeCompare(b.toString()));
  }, [candidates]);

  // Use imported columns from commonConstants
  const columns = CANDIDATE_TABLE_COLUMNS;

  // Sort filtered candidates
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredCandidates;

    const sorted = [...filteredCandidates].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Compare values
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [filteredCandidates, sortConfig]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'experience') {
      const parsed = parseInt(value, 10);
      const clamped = value === '' ? '' : isNaN(parsed) ? 0 : Math.min(30, Math.max(0, parsed));
      setFilters((prev) => ({ ...prev, [name]: clamped.toString() }));
      return;
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    setFilters({
      name: "",
      role: "",
      location: "",
      skills: "",
      experience: "",
      workType: "",
      feedbackStatus: "",
      readyToTravel: "",
      remoteAvailable: "",
      source: "",
      // noticePeriod: "", // COMMENTED OUT - Notice period filter disabled
      availableFromStart: "",
      availableFromEnd: ""
    });
    setCurrentPage(1);
  };

  const deleteRow = (rowId) => {
    setCandidates((prev) => prev.filter((candidate) => candidate.id !== rowId));
  };

  return (
    <>
      {/* Error Popup */}
      <PopupMessage
        visible={showErrorPopup}
        title="❌ Error Loading Candidates"
        message={error || 'An error occurred'}
        type="error"
        onClose={() => setShowErrorPopup(false)}
      />

    <div className="scrapper-page-wrapper">
      <div className="scrapper-header-section">
      </div>

      <form className="scrapper-filter-box">
        <div className="scrapper-filter-group">
          <label className="scrapper-label">Candidate Name</label>
          <input
            type="text"
            name="name"
            placeholder="Search by name..."
            value={filters.name}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Role</label>
          <select
            name="role"
            value={filters.role}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Location</label>
          <select
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Locations</option>
            {currentLocations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Skills</label>
          <select
            name="skills"
            value={filters.skills}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Skills</option>
            {[...new Set([...primarySkillOptions, ...secondarySkillOptions])].map((skill) => (
              <option key={skill} value={skill}>
                {skill}
              </option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Experience (Years)</label>
          <input
            type="number"
            min="0"
            max="30"
            name="experience"
            placeholder="0-30"
            value={filters.experience}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Work Type</label>
          <select
            name="workType"
            value={filters.workType || ''}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Work Types</option>
            {workTypeOptions.map((workType) => (
              <option key={workType} value={workType}>
                {workType}
              </option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Feedback Status</label>
          <select
            name="feedbackStatus"
            value={filters.feedbackStatus}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Status</option>
            {feedbackStatusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Ready to Travel</label>
          <select
            name="readyToTravel"
            value={filters.readyToTravel}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Remote Available</label>
          <select
            name="remoteAvailable"
            value={filters.remoteAvailable}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Available From Start</label>
          <input
            type="date"
            name="availableFromStart"
            value={filters.availableFromStart}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Available From End</label>
          <input
            type="date"
            name="availableFromEnd"
            value={filters.availableFromEnd}
            onChange={handleFilterChange}
            className="scrapper-input"
          />
        </div>

        <div className="scrapper-filter-group">
          <label className="scrapper-label">Source</label>
          <select
            name="source"
            value={filters.source}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Sources</option>
            {sourceOptions.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        {/* COMMENTED OUT - Notice period filter disabled */}
        {/*
        <div className="scrapper-filter-group">
          <label className="scrapper-label">Notice Period</label>
          <select
            name="noticePeriod"
            value={filters.noticePeriod}
            onChange={handleFilterChange}
            className="scrapper-input"
          >
            <option value="">All Notice Periods</option>
            {noticePeriodOptions.map((period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </select>
        </div>
        */}

        <button type="button" className="scrapper-filter-btn" onClick={handleFilterReset}>
          Reset
        </button>
      </form>

      <div className="scrapper-table-wrapper scrapper-table-wrapper-scroll">
        <table className="scrapper-table scrapper-table-collapse">
          <thead>
            <tr>
              {columns.map((col) => {
                let columnClass = "scrapper-th";
                if (col.isAction) {
                  columnClass += " scrapper-th-actions actions-column";
                } else {
                  columnClass += " scrapper-th-sortable";
                  // Add specific column width classes
                  switch (col.key) {
                    case 'resumeFile':
                      columnClass += " resume-column";
                      break;
                    case 'experience':
                      columnClass += " experience-column";
                      break;
                    case 'email':
                      columnClass += " email-column";
                      break;
                    case 'remoteAvailable':
                      columnClass += " remote-column";
                      break;
                    case 'phone':
                      columnClass += " phone-column";
                      break;
                    case 'readyToTravel':
                      columnClass += " ready-to-travel-column";
                      break;
                    default:
                      break;
                  }
                }
                return (
                  <th
                    key={col.key}
                    className={columnClass}
                    onClick={col.isAction || !col.isSortable ? undefined : () => handleSort(col.key)}
                    title={col.isAction || !col.isSortable ? undefined : "Click to sort"}
                  >
                    {col.label}
                    {!col.isAction && col.isSortable && sortConfig.key === col.key ? (
                      <span className="scrapper-sort-indicator">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    ) : !col.isAction && col.isSortable && (
                      <span className="scrapper-sort-unsorted">↕</span>
                    )}
                  </th>
                );
              })}
            </tr>
            </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="scrapper-no-data scrapper-loading-cell">
                  <div className="scrapper-loading-container">
                    <span className="scrapper-spinner-loader"></span>
                    Loading candidates...
                  </div>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="scrapper-no-data">
                  No candidates found.
                </td>
              </tr>
            ) : (() => {
              // Calculate pagination
              const totalPages = Math.ceil(sortedData.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedData = sortedData.slice(startIndex, endIndex);

              return paginatedData.map((candidate, idx) => (
                <tr key={`${candidate.id}-${idx}`} className="scrapper-tr-hover">
                  {columns.map((col) => {
                    let cellClass = "scrapper-td";
                    // Add specific column width classes
                    switch (col.key) {
                      case 'actions':
                        cellClass += " actions-column";
                        break;
                      case 'resumeFile':
                        cellClass += " resume-column";
                        break;
                      case 'experience':
                        cellClass += " experience-column";
                        break;
                      case 'email':
                        cellClass += " email-column";
                        break;
                      case 'remoteAvailable':
                        cellClass += " remote-column";
                        break;
                      case 'phone':
                        cellClass += " phone-column";
                        break;
                      case 'readyToTravel':
                        cellClass += " ready-to-travel-column";
                        break;
                      default:
                        break;
                    }
                    
                    if (col.isAction) {
                      return (
                        <td key={col.key} className={cellClass}>
                          <div className="scrapper-action-buttons">
                            <button
                              type="button"
                              className="edit-btn scrapper-action-button"
                              onClick={() => {
                                navigate('/resume', {
                                  state: {
                                    candidateData: candidate,
                                    isEditMode: true
                                  }
                                });
                              }}
                            >
                              Edit
                            </button>
                            {/* <button
                              type="button"
                              className="delete-btn scrapper-action-button"
                              onClick={() => deleteRow(candidate.id)}
                            >
                              Delete
                            </button> */}
                          </div>
                        </td>
                      );
                    }
                    let cellContent;
                    switch (col.key) {
                      case 'srNo':
                        cellContent = startIndex + idx + 1;
                        break;
                      case 'id':
                        cellContent = startIndex + idx + 1;
                        break;
                      case 'name':
                        cellContent = (
                          <a
                            href={`#/feedback/${candidate.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/feedback/${candidate.id}`, {
                                state: {
                                  candidate: {
                                    id: candidate.id,
                                    name: candidate.name,
                                    email: candidate.email,
                                    role: candidate.role,
                                    phone: candidate.phone,
                                    location: candidate.location
                                  }
                                }
                              });
                            }}
                            className="scrapper-link"
                            title="View feedback for this candidate"
                          >
                            {candidate.name}
                          </a>
                        );
                        break;
                      case 'email':
                        cellContent = <a href={`mailto:${candidate.email}`} className="scrapper-link">{candidate.email}</a>;
                        break;
                      case 'readyToTravel':
                        cellContent = <span className={`scrapper-status-badge ${candidate.readyToTravel ? 'scrapper-status-feedback' : ''}`}>{candidate.readyToTravel ? 'Yes' : 'No'}</span>;
                        break;
                      case 'remoteAvailable':
                        cellContent = <span className={`scrapper-status-badge ${candidate.remoteAvailable ? 'scrapper-status-feedback' : ''}`}>{candidate.remoteAvailable ? 'Yes' : 'No'}</span>;
                        break;
                      case 'availableFrom':
                        cellContent = candidate.availableFrom ? new Date(candidate.availableFrom).toLocaleDateString() : 'Not specified';
                        break;
                      case 'appliedDate':
                        cellContent = candidate.appliedDate ? new Date(candidate.appliedDate).toLocaleDateString() : 'N/A';
                        break;
                      case 'resumeFile':
                        cellContent = candidate.resumeUrl ? (
                          <a
                            href={candidate.resumeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="scrapper-link scrapper-resume-link-styled"
                          >
                            Resume
                          </a>
                        ) : (
                          <span className="scrapper-no-resume-text">No Resume</span>
                        );
                        break;
                      case 'feedbackStatus':
                        cellContent = <span className={`scrapper-status-badge scrapper-status-feedback`}>{candidate.feedbackStatus || 'No feedback'}</span>;
                        break;
                      // COMMENTED OUT - Feedback comment column disabled
                      /*
                      case 'feedbackComment':
                        cellContent = candidate.feedbackComment || 'No feedback';
                        break;
                      */
                      default:
                        cellContent = candidate[col.key] || 'Not specified';
                    }
                    let className = "scrapper-td";
                    // Add specific column width classes
                    switch (col.key) {
                      case 'actions':
                        className += " actions-column";
                        break;
                      case 'resumeFile':
                        className += " resume-column";
                        break;
                      case 'experience':
                        className += " experience-column";
                        break;
                      case 'remoteAvailable':
                        className += " remote-column";
                        break;
                      case 'phone':
                        className += " phone-column";
                        break;
                      case 'email':
                        className += " email-column";
                        break;
                      case 'readyToTravel':
                        className += " ready-to-travel-column";
                        break;
                      default:
                        break;
                    }
                    if (['primarySkills', 'secondarySkills', 'techStack'].includes(col.key)) {
                      // COMMENTED OUT - Feedback comment column disabled
                      // if (['primarySkills', 'secondarySkills', 'techStack', 'feedbackComment'].includes(col.key)) {
                      className += " scrapper-cell-wrap";
                    }
                    return <td key={col.key} className={className}>{cellContent}</td>;
                  })}
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {sortedData.length > 0 && (() => {
        const totalPages = Math.ceil(sortedData.length / itemsPerPage);
        return (
          <div className="scrapper-pagination-container">
            <div className="scrapper-pagination-items">
              <label className="scrapper-pagination-items-label">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="scrapper-pagination-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="scrapper-pagination-nav">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="scrapper-pagination-button"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="scrapper-pagination-button"
              >
                ← Prev
              </button>

              <div className="scrapper-pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`scrapper-pagination-page-button ${currentPage === pageNum ? 'scrapper-pagination-page-button-active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="scrapper-pagination-button"
              >
                Next →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="scrapper-pagination-button"
              >
                Last
              </button>
            </div>

            <div className="scrapper-pagination-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({sortedData.length} total)
            </div>
          </div>
        );
      })()}
    </div>
    </>
  );
};

export default CandidatePage;
